import React, { useMemo, useState } from "react";
import styled from "styled-components";
import {
  Modal,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Link } from "react-router-dom";
import useAuth from "../../hooks/useAuth"; // ajusta ruta

const AuthModal = ({ open, onClose, codeWin, winData }) => {
  const { login, register, forgotPassword } = useAuth();

  const [panel, setPanel] = useState("login"); // "login" | "register" | "forgot"
  const isLogin = panel === "login";
  const isForgot = panel === "forgot";

  // Forgot password
  const [forgotEmail, setForgotEmail] = useState("");

  const [showPass, setShowPass] = useState(false);
  const [showPass2, setShowPass2] = useState(false);

  // Login
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [stay, setStay] = useState(true);

  // Register
  const [name, setName] = useState("");
  const [lastname, setLastname] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [document, setDocument] = useState("");
  const [phone, setPhone] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regPassword2, setRegPassword2] = useState("");
  const [agree, setAgree] = useState(true);

  const title = useMemo(
    () => (isForgot ? "Recuperar contraseña" : isLogin ? "Ingresar" : "Crear cuenta"),
    [isLogin, isForgot]
  );

  const handleClose = () => {
    setShowPass(false);
    setShowPass2(false);
    onClose?.();
  };

  const handleSubmitLogin = (e) => {
    e.preventDefault();
    login({
      email,
      password,
      session: stay,
      codeWin,
      winData,
    });
  };

  const handleSubmitForgot = async (e) => {
    e.preventDefault();
    const data = await forgotPassword(forgotEmail);
    if (data?.success) {
      setForgotEmail("");
      setPanel("login");
    }
  };

  const handleSubmitRegister = (e) => {
    e.preventDefault();
    register({
      name,
      lastname,
      email: regEmail,
      document,
      phone,
      password: regPassword,
      password_confirmation: regPassword2,
      agree_terms: agree ? true : null,
      codeWin,
      winData,
    });
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Overlay>
        <Card role="dialog" aria-modal="true">
          {/* Sticky top */}
          <TopSticky>
            <Header>
              <Typography className="title">{title}</Typography>
              <IconButton
                className="closeBtn"
                onClick={handleClose}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Header>

            <Tabs>
              <TabBtn
                type="button"
                className={isLogin ? "active" : ""}
                onClick={() => setPanel("login")}
              >
                Ingresar
              </TabBtn>
              <TabBtn
                type="button"
                className={panel === "register" ? "active" : ""}
                onClick={() => setPanel("register")}
              >
                Registrarme
              </TabBtn>
            </Tabs>
          </TopSticky>

          {/* Scrollable content */}
          <Body>
            {isForgot ? (
              <Form onSubmit={handleSubmitForgot} noValidate>
                <BottomHint style={{ textAlign: "left", marginBottom: 4 }}>
                  No te preocupes, puedes recuperarla. Ingresa tu correo y te
                  enviaremos un enlace para restablecerla.
                </BottomHint>

                <FieldLabel>Correo electrónico</FieldLabel>
                <StyledTextField
                  fullWidth
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="Correo electrónico"
                  type="email"
                  autoComplete="email"
                />

                <PrimaryBtn type="submit">Enviar enlace</PrimaryBtn>

                <BottomHint>
                  <a
                    href="#/"
                    onClick={(e) => {
                      e.preventDefault();
                      setPanel("login");
                    }}
                  >
                    Volver a iniciar sesión
                  </a>
                </BottomHint>
              </Form>
            ) : isLogin ? (
              <Form onSubmit={handleSubmitLogin} noValidate>
                <FieldLabel>Correo electrónico</FieldLabel>
                <StyledTextField
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Correo electrónico"
                  type="email"
                  autoComplete="email"
                />

                <FieldLabel>Contraseña</FieldLabel>
                <StyledTextField
                  fullWidth
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Contraseña"
                  type={showPass ? "text" : "password"}
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPass((s) => !s)}
                          edge="end"
                          size="small"
                          aria-label="Mostrar contraseña"
                        >
                          {showPass ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <RowBetween>
                  <CheckboxRow>
                    <input
                      id="stay"
                      type="checkbox"
                      checked={stay}
                      onChange={(e) => setStay(e.target.checked)}
                    />
                    <label htmlFor="stay">Mantener sesión iniciada</label>
                  </CheckboxRow>

                  <SmallLink
                    href="#/"
                    onClick={(e) => {
                      e.preventDefault();
                      setForgotEmail(email);
                      setPanel("forgot");
                    }}
                  >
                    ¿Olvidaste tu contraseña?
                  </SmallLink>
                </RowBetween>

                <PrimaryBtn type="submit">Ingresar</PrimaryBtn>
              </Form>
            ) : (
              <Form onSubmit={handleSubmitRegister} noValidate>
                <NameRow>
                  <NameField>
                    <FieldLabel>Nombre</FieldLabel>
                    <StyledTextField
                      fullWidth
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Nombre"
                      type="text"
                      autoComplete="given-name"
                    />
                  </NameField>

                  <NameField>
                    <FieldLabel>Apellido</FieldLabel>
                    <StyledTextField
                      fullWidth
                      value={lastname}
                      onChange={(e) => setLastname(e.target.value)}
                      placeholder="Apellido"
                      type="text"
                      autoComplete="family-name"
                    />
                  </NameField>
                </NameRow>

                <FieldLabel>Correo electrónico</FieldLabel>
                <StyledTextField
                  fullWidth
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  placeholder="Correo electrónico"
                  type="email"
                  autoComplete="email"
                />

                <FieldLabel>Documento</FieldLabel>
                <StyledTextField
                  fullWidth
                  value={document}
                  onChange={(e) => setDocument(e.target.value)}
                  placeholder="Documento"
                  type="text"
                  autoComplete="off"
                />

                <FieldLabel>Celular</FieldLabel>
                <StyledTextField
                  fullWidth
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Celular"
                  type="text"
                  autoComplete="off"
                />

                <FieldLabel>Contraseña</FieldLabel>
                <StyledTextField
                  fullWidth
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  placeholder="Contraseña"
                  type={showPass ? "text" : "password"}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPass((s) => !s)}
                          edge="end"
                          size="small"
                          aria-label="Mostrar contraseña"
                        >
                          {showPass ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <FieldLabel>Repite la contraseña</FieldLabel>
                <StyledTextField
                  fullWidth
                  value={regPassword2}
                  onChange={(e) => setRegPassword2(e.target.value)}
                  placeholder="Repite la contraseña"
                  type={showPass2 ? "text" : "password"}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPass2((s) => !s)}
                          edge="end"
                          size="small"
                          aria-label="Mostrar contraseña"
                        >
                          {showPass2 ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <CheckboxRow>
                  <input
                    id="agree"
                    type="checkbox"
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                  />
                  <label htmlFor="agree">
                    Acepto los{" "}
                    <Link to="/terms-conditions">Términos & Condiciones</Link>
                  </label>
                </CheckboxRow>

                <PrimaryBtn type="submit">Crear cuenta</PrimaryBtn>

                <BottomHint>
                  ¿Ya tienes cuenta?{" "}
                  <a
                    href="#/"
                    onClick={(e) => {
                      e.preventDefault();
                      setPanel("login");
                    }}
                  >
                    Ingresa aquí
                  </a>
                </BottomHint>
              </Form>
            )}
          </Body>
        </Card>
      </Overlay>
    </Modal>
  );
};

export default AuthModal;

/* ================= styles ================= */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: clamp(10px, 3vw, 14px); /* en móvil reduce padding */
`;

const Card = styled.div`
  /* Desktop / tablet */
  width: min(620px, 44vw);
  max-width: 92vw;

  /* Altura segura */
  max-height: min(86vh, 720px);

  background: #fff;
  border-radius: 14px;
  box-shadow: 0 22px 55px rgba(0, 0, 0, 0.45);

  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* ✅ MÓVIL: que no quede angosto */
  @media (max-width: 600px) {
    width: min(520px, 94vw); /* casi todo el ancho del celular */
    max-width: 94vw;
    max-height: 88vh; /* un poco más alto, pero sin salirse */
    border-radius: 12px;
  }

  /* ✅ pantallas MUY pequeñas (celulares compactos) */
  @media (max-width: 360px) {
    width: 96vw;
    max-width: 96vw;
    max-height: 90vh;
  }
`;

const TopSticky = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  padding: 14px 14px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;

  .title {
    font-weight: 900;
    font-size: clamp(1.2rem, 2vw, 1.8rem);
    line-height: 1.05;
    color: #1a1d28;
  }

  .closeBtn {
    border: 2px solid rgba(0, 0, 0, 0.25);
    border-radius: 10px;
  }
`;

const Tabs = styled.div`
  margin-top: 10px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const TabBtn = styled.button`
  border: 3px solid #1a1d28;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  height: 42px;

  font-weight: 900;
  font-size: 0.95rem;
  color: #1a1d28;

  &.active {
    background: #743fe3;
    border-color: #743fe3;
    color: #fff;
  }
`;

const Body = styled.div`
  padding: 12px 14px 16px;

  /* CLAVE: scroll interno del modal */
  overflow: auto;

  /* mejor scroll en iOS */
  -webkit-overflow-scrolling: touch;
`;

const Form = styled.form`
  display: grid;
  gap: 8px;
`;

/* Fila de nombre + apellido: en desktop van lado a lado, en mobile
   se apilan. Mantengo el gap=8px para que coincida con el grid del
   resto del formulario. */
const NameRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

/* Celda interna de la fila: agrupa label + input para que la grid
   externa cuente cada par como una columna. */
const NameField = styled.div`
  display: grid;
  gap: 4px;
  min-width: 0;
`;

const FieldLabel = styled.p`
  margin: 0;
  font-weight: 900;
  color: rgba(26, 29, 40, 0.78);
  font-size: 0.92rem;
`;

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 14px;
    background: #fff;

    .MuiOutlinedInput-notchedOutline {
      border: 3px solid #1a1d28;
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: #1a1d28;
      box-shadow: 0 0 0 4px rgba(116, 63, 227, 0.16);
    }
  }

  input {
    height: 40px; /* más compacto */
    padding: 10px 12px; /* menos padding */
    font-weight: 800;
    font-size: 1rem; /* más pequeño */
  }
`;

const RowBetween = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 2px;
`;

const CheckboxRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  input {
    width: 16px;
    height: 16px;
  }

  label {
    font-weight: 800;
    font-size: 0.92rem;
    color: rgba(26, 29, 40, 0.75);
  }

  a {
    font-weight: 900;
    color: #743fe3;
    text-decoration: none;
  }
`;

const SmallLink = styled.a`
  font-weight: 900;
  font-size: 0.92rem;
  color: #743fe3;
  text-decoration: none;
`;

const PrimaryBtn = styled(Button).attrs({
  variant: "contained",
})`
  && {
    margin-top: 8px;
    width: 100%;
    height: 50px; /* más compacto */
    border-radius: 12px;
    background: #743fe3;
    color: #fff;
    font-weight: 900;
    font-size: 1.05rem;
    box-shadow: 0 14px 22px rgba(0, 0, 0, 0.16);

    &:hover {
      background: #743fe3;
      filter: brightness(1.05);
    }
  }
`;

const BottomHint = styled.div`
  margin-top: 6px;
  text-align: center;
  font-weight: 800;
  font-size: 0.95rem;
  color: rgba(26, 29, 40, 0.75);

  a {
    font-weight: 900;
    color: #743fe3;
    text-decoration: none;
  }
`;
